/**
 * Runs `task` over `items` with at most `limit` calls in flight, preserving
 * input order in the results.
 *
 * An unbounded `Promise.all` over a long list starts every request at once.
 * Under HTTP/1.1 the browser quietly queues past six per origin, but over
 * HTTP/2 they all become simultaneous streams on one connection and the ones
 * past the server's limit are reset, which reaches the user as
 * ERR_HTTP2_PROTOCOL_ERROR / "Failed to fetch".
 */
export const mapWithConcurrency = async <T, R>(
  items: readonly T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let next = 0;

  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const index = next++;
      results[index] = await task(items[index], index);
    }
  };

  const workers = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: workers }, () => worker()));

  return results;
};
